import { call, put, select, take } from "redux-saga/effects";
import { httpDelete, httpGet, httpPost, httpPut } from "../../services";
import {
  AccountConst,
  deleteRequestAccountGoogle2FAErrorAction,
  deleteRequestAccountGoogle2FALoadingAction,
  deleteRequestAccountGoogle2FASuccessAction,
  deleteRequestAccountSMS2FAErrorAction,
  deleteRequestAccountSMS2FALoadingAction,
  deleteRequestAccountSMS2FASuccessAction,
  getErrorAccountSiteCredential,
  getLoadAccountSiteCredential,
  getRequestAccount2FAErrorAction,
  getRequestAccount2FALoadingAction,
  getRequestAccount2FASuccessAction,
  getRequestAccountBackupCodesErrorAction,
  getRequestAccountBackupCodesLoadingAction,
  getRequestAccountBackupCodesSuccessAction,
  GetRequestAccountLoginSessionDataSchema,
  getRequestAccountResetBackupCodesErrorAction,
  getRequestAccountResetBackupCodesLoadingAction,
  getRequestAccountResetBackupCodesSuccessAction,
  getRequestErrorAction,
  getRequestLoadingAction,
  getRequestLoginSessionErrorAction,
  getRequestLoginSessionLoadingAction,
  getRequestLoginSessionSuccessAction,
  GetRequestProfileDataSchema,
  getRequestProfileSuccessAction,
  getSitesSubscriptionListErrorAction,
  getSitesSubscriptionListLoadingAction,
  getSitesSubscriptionListSuccessAction,
  getSuccessAccountSiteCredential,
  postRequestAccountGoogle2FAErrorAction,
  postRequestAccountGoogle2FALoadingAction,
  postRequestAccountGoogle2FASuccessAction,
  postRequestAccountSMS2FAErrorAction,
  postRequestAccountSMS2FALoadingAction,
  postRequestAccountSMS2FASuccessAction,
  postRequestAccountSMS2FAVerifyErrorAction,
  postRequestAccountSMS2FAVerifyLoadingAction,
  postRequestAccountSMS2FAVerifySuccessAction,
  ProfileRequestSchema,
  RequestSitesSubscriptionListResponse,
  SettingsRequestSchema,
  switchToOwnedSiteErrorAction,
  switchToOwnedSiteResetAction,
  switchToOwnedSiteSuccessAction,
  switchToSharedSiteErrorAction,
  switchToSharedSiteResetAction,
  switchToSharedSiteSuccessAction,
  updateErrorAccountSiteCredential,
  updateLoadAccountSiteCredential,
  updateRequestLogOutSessionErrorAction,
  updateRequestLogOutSessionLoadingAction,
  updateRequestLogOutSessionSuccessAction,
  UpdateRequestProfileDataSchema,
  updateRequestProfileErrorAction,
  updateRequestProfileLoadingAction,
  updateRequestProfileSuccessAction,
  updateSuccessAccountSiteCredential
} from "../actions/accountActions";
import { showLoadingAction } from "../actions/appStateActions";
import { AuthConst, getYourSitesAction } from "../actions/authActions";
import { RootSchema } from "../reducers";
function* getSitesSubscriptionListHandler() {
  try {
    yield put(getSitesSubscriptionListLoadingAction());
    let sitesAndAccountCodes = yield select(getSitesAndAccountCodes);
    // If the account's owned sites have not been fetched, do so before continuing:
    if (sitesAndAccountCodes === -1) {
      yield put(getYourSitesAction());
      yield take(AuthConst.GET_YOUR_SITES_SUCCESS);
      sitesAndAccountCodes = yield select(getSitesAndAccountCodes);
    }
    // Fetch the subscription type for each account based on its account code:
    const sitesSubscriptions: RequestSitesSubscriptionListResponse = [];
    const siteLength = sitesAndAccountCodes.length;
    let retriveIndex = 0;
    const getSiteSubscription = async () => {
      for (let i = 0; i < sitesAndAccountCodes.length; i++) {
        const response = await httpGet("/account/billing/subscription/list", {
          accountcode: sitesAndAccountCodes[i].accountcode,
          ind: i,
          isPreviousPlanApp: sitesAndAccountCodes[i].isPreviousPlanApp
        });
        sitesSubscriptions.push({
          AppName: sitesAndAccountCodes[i].AppName,
          Subscription: processSiteSubscriptionResponse(response)
        });
        retriveIndex = i;
      }
      if (retriveIndex === siteLength - 1) {
        return sitesSubscriptions;
      }
    };

    yield getSiteSubscription();
    yield put(getSitesSubscriptionListSuccessAction({ response: sitesSubscriptions }));
  } catch (e: any) {
    yield put(getSitesSubscriptionListErrorAction(e.message));
  }
}

/**
 * Navigate to the path provided after switching to the owned site whose app ID and name are provided as arguments.
 * @param appId ID of the owned site to switch to.
 * @param appName Name of the owned site to switch to.
 * @param path  Path to navigate to after switching to the site whose ID and path are provided.
 */
function* switchToOwnedSiteHandler(appId: number, appName: string, path: string) {
  try {
    yield put(showLoadingAction(true, "Please wait..."));
    const response = yield httpGet("/account/switchapp", { appid: appId });
    if (response.ErrorCode !== undefined) throw Error(response.Message || response.Description || "");
    commonHandlerForSiteSwitchingResponse(appId, appName, path, response);
    yield put(switchToOwnedSiteSuccessAction());
  } catch (e: any) {
    yield put(showLoadingAction(false));
    yield put(switchToOwnedSiteErrorAction(`Unsuccessful site switch to ${appName}. ${e.message}`));
    yield put(switchToOwnedSiteResetAction()); // A future request to switch to owned site might be successful, so reset status report of the request.
  }
}

/**
 * Navigate to the path provided after switching to the shared site whose app ID and name are provided as arguments.
 * @param appId ID of the owned site to switch to.
 * @param appName Name of the owned site to switch to.
 * @param path  Path to navigate to after switching to the site whose ID and path are provided.
 */
function* switchToSharedSiteHandler(appId: number, appName: string, path: string) {
  try {
    const response = yield httpGet("/account/switchsharedsite", {
      appid: appId
    });
    commonHandlerForSiteSwitchingResponse(appId, appName, path, response);
    yield put(switchToSharedSiteSuccessAction());
  } catch (e: any) {
    yield put(switchToSharedSiteErrorAction(`Unsuccessful site switch to ${appName}. ${e.message}`));
    yield put(switchToSharedSiteResetAction()); // A future request to switch to shared site might be successful, so reset status report of the request.
  }
}

/**
 * Common actions performed after receiving a successful response from the site switching endpoints: /account/switchapp and
 * /account/switchsahredsite.
 * @param appId ID of the owned site to switch to.
 * @param appName Name of the owned site to switch to.
 * @param path  Path to navigate to after switching to the site whose ID and path are provided.
 * @param response  a response from either of the /account/switchapp and /account/switchsahredsite endpoints.
 */
export async function commonHandlerForSiteSwitchingResponse(appId: number, appName: string, path: string, response: any) {
  try {
    if (response.xtoken === "" && location.pathname !== "/logout") location.href = "/logout";
    else {
      localStorage.setItem("siteName", appName);
      window.history.replaceState({}, "");
      location.href = path;
    }
  } catch (e: any) {
    location.href = path;
  }
}

/**
 * Returns an array of {AppName: string, accountcode: string} objects, where AppName is the name
 * of one of the sites the account owns and aaccountcode its recurly account code; if the state
 * does not have the list of the sites the account owns yet, returns -1. If the account does not own
 * any sites, returns an empty object.
 * @param state State of the application's redux store.
 */
const getSitesAndAccountCodes = (state: RootSchema) => {
  if (!state.auth.yourSites.response) return -1;
  else if (state.auth.yourSites.response.length === 0) return {};
  return state.auth.yourSites.response.map((site) => {
    return {
      AppName: site.AppName,
      accountcode: site.RecurlyAccountCode || site.AppId,
      isPreviousPlanApp: site.RecurlyAccountCode ? true : false
    };
  });
};

/**
 * Process response sent to the GET /account/billing/subscription/list endpoint and returns the subscription associated
 * to the site whose data was sent in the request to the endpoint.
 * @param siteSubscriptionResponse
 */
const processSiteSubscriptionResponse = (siteSubscriptionResponse: any) => {
  let subscription = "Free";
  if (siteSubscriptionResponse !== null && siteSubscriptionResponse.resp !== null && siteSubscriptionResponse.resp.data !== null && siteSubscriptionResponse.resp.data !== 404) {
    try {
      subscription =
        siteSubscriptionResponse.resp.data.subscriptions.subscription instanceof Array
          ? siteSubscriptionResponse.resp.data.subscriptions.subscription[0].plan.name
          : siteSubscriptionResponse.resp.data.subscriptions.subscription.plan.name;
    } catch (err) {
      subscription = "Free";
    }
  }
  return subscription;
};
function* getRequestProfileHandler() {
  try {
    yield put(getRequestLoadingAction());
    const data: GetRequestProfileDataSchema = yield httpGet("/account/user");
    //yield put(showLoadingAction(false));
    yield put(getRequestProfileSuccessAction(data));
  } catch (e: any) {
    yield put(getRequestErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* updateRequestProfileHandler(payload: ProfileRequestSchema) {
  try {
    yield put(updateRequestProfileLoadingAction());
    const data: UpdateRequestProfileDataSchema = yield httpPut("/account/user", {}, payload);
    yield put(updateRequestProfileSuccessAction(data));
  } catch (e: any) {
    yield put(updateRequestProfileErrorAction(e.message));
  } finally {
    //    yield put(showLoadingAction(false));
  }
}

function* getAccountCredentialHandler() {
  try {
    yield put(getLoadAccountSiteCredential());

    const data: any = yield httpGet("/account/site/credentials");
    yield put(getSuccessAccountSiteCredential(data));
  } catch (e: any) {
    yield put(getErrorAccountSiteCredential(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* updateAccountSettingsHandler(payload: SettingsRequestSchema) {
  try {
    yield put(updateLoadAccountSiteCredential());
    const data: any = yield httpPut("/account/password", {}, payload);
    yield put(updateSuccessAccountSiteCredential(data));
  } catch (e: any) {
    yield put(updateErrorAccountSiteCredential(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getRequestLoginSessionHandler() {
  try {
    yield put(getRequestLoginSessionLoadingAction());
    const data: GetRequestAccountLoginSessionDataSchema = yield httpGet("/account/sessions", {});
    yield put(getRequestLoginSessionSuccessAction(data));
  } catch (e: any) {
    yield put(getRequestLoginSessionErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* updateRequestLogOutSessionHandler(payload: any) {
  try {
    yield put(updateRequestLogOutSessionLoadingAction());
    const data: any = yield httpPost("/team/invalidate/all", {}, payload);
    yield put(updateRequestLogOutSessionSuccessAction(data));
  } catch (e: any) {
    yield put(updateRequestLogOutSessionErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getRequestAccount2FAHandler() {
  try {
    yield put(getRequestAccount2FALoadingAction());
    const data: GetRequestAccountLoginSessionDataSchema = yield httpGet("/account/2fa");
    yield put(getRequestAccount2FASuccessAction(data));
  } catch (e: any) {
    yield put(getRequestAccount2FAErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* postRequestAccountGoogle2FAHandler(payload: any) {
  try {
    yield put(postRequestAccountGoogle2FALoadingAction());
    const data: any = yield httpPost("/account/2fa/google", {}, payload);
    if (data.ErrorCode) {
      throw new Error(data.Description || data.Message || "");
    } else {
      yield put(postRequestAccountGoogle2FASuccessAction(data));
    }
  } catch (e: any) {
    yield put(postRequestAccountGoogle2FAErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* deleteRequestAccountGoogle2FAHandler(payload: any) {
  try {
    yield put(deleteRequestAccountGoogle2FALoadingAction());
    const data: any = yield httpDelete("/account/2fa/google", {}, payload);
    yield put(deleteRequestAccountGoogle2FASuccessAction(data));
  } catch (e: any) {
    yield put(deleteRequestAccountGoogle2FAErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* postRequestAccountSMS2FAVerifyHandler(payload: any) {
  try {
    yield put(postRequestAccountSMS2FAVerifyLoadingAction());
    const data: any = yield httpPost("/account/2fa/sms/verification", {}, payload);
    yield put(postRequestAccountSMS2FAVerifySuccessAction(data));
  } catch (e: any) {
    yield put(postRequestAccountSMS2FAVerifyErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* postRequestAccountSMS2FAHandler(payload: any) {
  try {
    yield put(postRequestAccountSMS2FALoadingAction());
    const data: any = yield httpPost("/account/2fa/sms", {}, payload);
    if (data.ErrorCode) {
      throw new Error(data.Description || data.Message || "");
    } else {
      yield put(postRequestAccountSMS2FASuccessAction(data));
    }
  } catch (e: any) {
    yield put(postRequestAccountSMS2FAErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* deleteRequestAccountSMS2FAHandler(payload: any) {
  try {
    yield put(deleteRequestAccountSMS2FALoadingAction());
    const data: any = yield httpDelete("/account/2fa/sms", {}, payload);
    yield put(deleteRequestAccountSMS2FASuccessAction(data));
  } catch (e: any) {
    yield put(deleteRequestAccountSMS2FAErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getRequestAccountBackupCodesHandler(queryParams: any) {
  try {
    yield put(getRequestAccountBackupCodesLoadingAction());
    const data: any = yield httpGet("/account/backupcodes", queryParams);
    yield put(getRequestAccountBackupCodesSuccessAction(data));
  } catch (e: any) {
    yield put(getRequestAccountBackupCodesErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getRequestAccountResetBackupCodesHandler(queryParams: any) {
  try {
    yield put(getRequestAccountResetBackupCodesLoadingAction());
    const data: any = yield httpGet("/account/backupcodes/reset", queryParams);
    yield put(getRequestAccountResetBackupCodesSuccessAction(data));
  } catch (e: any) {
    yield put(getRequestAccountResetBackupCodesErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

/**
 * Watchers
 */
export function* watchGetProfileData() {
  while (true) {
    yield take(AccountConst.GET_REQUEST_PROFILE);
    yield call(getRequestProfileHandler);
  }
}

export function* watchUpdateProfileData() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.UPDATE_REQUEST_PROFILE_DATA);
    yield call(updateRequestProfileHandler, payload);
  }
}

export function* watchGetAccountCredentialData() {
  while (true) {
    yield take(AccountConst.GET_REQUEST_ACCOUNT_SITE_CREDENTIAL);
    yield call(getAccountCredentialHandler);
  }
}

export function* watchUpdateAccountSettingsData() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.UPDATE_REQUEST_ACCOUNT_SITE_SETTING);
    yield call(updateAccountSettingsHandler, payload);
  }
}

export function* watchGetLoginSessionData() {
  while (true) {
    yield take(AccountConst.GET_REQUEST_ACCOUNT_SESSION);
    yield call(getRequestLoginSessionHandler);
  }
}

export function* watchUpdateLogOutSession() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.UPDATE_REQUEST_ACCOUNT_SESSION_LOGOUT);
    yield call(updateRequestLogOutSessionHandler, payload);
  }
}

export function* watchGetSitesSubscriptionList() {
  while (true) {
    yield take(AccountConst.GET_SITES_SUBSCRIPTION_LIST);
    yield call(getSitesSubscriptionListHandler);
  }
}

export function* watchSwitchToOwnedSite() {
  while (true) {
    const {
      payload: { appId, appName, path }
    } = yield take(AccountConst.SWITCH_TO_OWNED_SITE);
    yield call(switchToOwnedSiteHandler, appId, appName, path);
  }
}

export function* watchSwitchToSharedSite() {
  while (true) {
    const {
      payload: { appId, appName, path }
    } = yield take(AccountConst.SWITCH_TO_SHARED_SITE);
    yield call(switchToSharedSiteHandler, appId, appName, path);
  }
}

export function* watchGetAccount2FAData() {
  while (true) {
    yield take(AccountConst.GET_REQUEST_ACCOUNT_2FA);
    yield call(getRequestAccount2FAHandler);
  }
}

export function* watchPostAccountGoogle2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.POST_REQUEST_ACCOUNT_GOOGLE_2FA);
    yield call(postRequestAccountGoogle2FAHandler, payload);
  }
}

export function* watchDeleteAccountGoogle2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.DELETE_REQUEST_ACCOUNT_GOOGLE_2FA);
    yield call(deleteRequestAccountGoogle2FAHandler, payload);
  }
}

export function* watchPostAccountSMS2FAVerify() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.POST_REQUEST_ACCOUNT_SMS_VERIFY_2FA);
    yield call(postRequestAccountSMS2FAVerifyHandler, payload);
  }
}

export function* watchPostAccountSMS2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.POST_REQUEST_ACCOUNT_SMS_2FA);
    yield call(postRequestAccountSMS2FAHandler, payload);
  }
}

export function* watchDeleteAccountSMS2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.DELETE_REQUEST_ACCOUNT_SMS_2FA);
    yield call(deleteRequestAccountSMS2FAHandler, payload);
  }
}

export function* watchGetAccountBackupCodes2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.GET_REQUEST_ACCOUNT_BACKUP_CODES);
    yield call(getRequestAccountBackupCodesHandler, payload);
  }
}

export function* watchGetAccountResetBackupCodes2FA() {
  while (true) {
    const { payload: payload } = yield take(AccountConst.GET_REQUEST_ACCOUNT_RESET_BACKUP_CODES);
    yield call(getRequestAccountResetBackupCodesHandler, payload);
  }
}
